/**
 * VedangJyotisham V6.2.0 — Muhurta Phase 18 Samskara-Specific Rules Engine
 *
 * FORENSIC AUDIT & CLASSICAL PROVENANCE CLOSURE:
 * 1. Classical Research Findings:
 *    - In primary classical Jyotish shastras (Brihat Samhita, Muhurta Chintamani,
 *      Muhurta Martanda, Kalaprakasika, Dharma Sindhu, Nirnaya Sindhu), Samskara-specific
 *      Muhurta evaluation involves multi-factor canonical requirements:
 *        a) Vivaha (Marriage): Solar month (Sun must transit auspicious signs; Kharmas /
 *           Sun in Sagittarius/Pisces prohibited), Guru/Shukra Asta (combustion) strictly
 *           prohibited, Guru/Surya/Chandra Bala for bride/groom, Lagna Shuddhi (8th house
 *           vacant), and specific pada-level Gandanta exclusions (Mula, Magha, Revati).
 *        b) Upanayana: Uttarayana (Sun moving North), non-combustion of Jupiter/Venus,
 *           age from conception/birth, and Surya/Guru Bala for the initiate.
 *        c) Griha Pravesh: Distinct rules for Apoorva (new), Sapoorva (old), and Dwandva
 *           (renovation), specific solar months, and Lagna strength.
 *        d) Namakarana / Annaprashana / Chudakarana / Vidyarambha: Specific day/month/year
 *           milestones from birth combined with Tithi, Nakshatra, and Lagna requirements.
 *    - The static arrays of `approvedTithis` and `approvedNakshatras` defined below represent
 *      an informal, modern preference heuristic. They do NOT evaluate solar month transit,
 *      planetary combustion, Lagna shuddhi, or birth-chart specific strength.
 *    - No Tier 1 Sanskrit verse justifies reducing complex Samskara evaluation to a static
 *      tithi/nakshatra lookup table.
 *
 * 2. Decision Gate Disposition (OUTCOME B — QUARANTINED AS INFORMATIONAL):
 *    - The static preference arrays are retained strictly as a quarantined research artifact
 *      to maintain modular validation architecture without breaking downstream consumers.
 *    - Quarantined invariants:
 *        classification = 'INFORMATIONAL'
 *        eligibility = 'INFORMATIONAL'
 *        scoreImpact = 0
 *        provenanceStatus = 'UNVERIFIED'
 *        confidence = 'INSUFFICIENT'
 *    - Under no circumstances can Phase 18 assert HARD_EXCLUSION, WARNING, or SUPPORT outcomes,
 *      nor can it reject candidates, apply ranking penalties, or influence Muhurta scoring.
 */

import { calculateAuthoritativePanchanga } from './panchangaEngine';

export interface SamskaraEvaluationResult {
  samskaraName: string;
  classification: 'SUPPORT' | 'WARNING' | 'HARD_EXCLUSION' | 'INFORMATIONAL' | 'NEUTRAL';
  eligibility: 'HARD_EXCLUSION' | 'WARNING' | 'SUPPORT' | 'INFORMATIONAL';
  scoreImpact: number;
  description: string;
  supportedSamskara: boolean;
  provenanceStatus: 'UNVERIFIED';
  confidence: 'INSUFFICIENT';
  ruleEvidence: {
    tithiNum: number;
    nakshatraName: string;
    varaName: string;
    matchesConfiguredTithiPreference: boolean;
    matchesConfiguredNakshatraPreference: boolean;
  };
}

// These Phase 18 arrays are retained as historical/configured preferences.
// They lack classical provenance for prescriptive Muhurta selection and
// MUST NOT drive SUPPORT, WARNING, or HARD_EXCLUSION outcomes.
const SAMSKARA_RULES: Record<string, { approvedTithis: number[]; approvedNakshatras: string[] }> = {
  vivaha: {
    approvedTithis: [2, 3, 5, 7, 10, 11, 13, 17, 18, 20, 22, 25, 26, 28],
    approvedNakshatras: ['Rohini', 'Mrigashira', 'Magha', 'Uttara Phalguni', 'Hasta', 'Swati', 'Anuradha', 'Mula', 'Uttara Ashadha', 'Uttara Bhadrapada', 'Revati']
  },
  upanayana: {
    approvedTithis: [2, 3, 5, 7, 10, 13],
    approvedNakshatras: ['Mrigashira', 'Punarvasu', 'Pushya', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Dhanistha', 'Satabhisha']
  },
  namakarana: {
    approvedTithis: [2, 3, 5, 7, 10, 12, 13],
    approvedNakshatras: ['Rohini', 'Mrigashira', 'Punarvasu', 'Pushya', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Satabhisha', 'Revati']
  },
  annaprashana: {
    approvedTithis: [2, 3, 5, 7, 10, 13],
    approvedNakshatras: ['Rohini', 'Mrigashira', 'Pushya', 'Hasta', 'Swati', 'Anuradha', 'Sravana', 'Revati']
  },
  chudakarana: {
    approvedTithis: [2, 3, 5, 7, 10, 13],
    approvedNakshatras: ['Ashwini', 'Pushya', 'Hasta', 'Chitra', 'Swati', 'Punarvasu', 'Sravana', 'Dhanistha']
  },
  vidyarambha: {
    approvedTithis: [5, 7, 10, 11, 13],
    approvedNakshatras: ['Mrigashira', 'Pushya', 'Hasta', 'Chitra', 'Swati', 'Anuradha', 'Sravana', 'Revati', 'Ashwini']
  },
  grihapravesh: {
    approvedTithis: [2, 3, 5, 7, 10, 11, 13],
    approvedNakshatras: ['Rohini', 'Mrigashira', 'Pushya', 'Uttara Phalguni', 'Uttara Ashadha', 'Uttara Bhadrapada', 'Revati']
  }
};

/**
 * Evaluates Samskara-specific Muhurta preferences for a given date, location, and Samskara type.
 * Quarantined as an informational research artifact with zero production score impact.
 */
export function evaluateSamskaraMuhurta(
  testDate: Date,
  latitude: number,
  longitude: number,
  samskaraType: string
): SamskaraEvaluationResult {
  const panchang = calculateAuthoritativePanchanga(testDate, { latitude, longitude });
  const key = samskaraType.toLowerCase().replace(/[^a-z]/g, '');
  const rules = SAMSKARA_RULES[key];

  if (!rules) {
    return {
      samskaraName: samskaraType,
      classification: 'INFORMATIONAL',
      eligibility: 'INFORMATIONAL',
      scoreImpact: 0,
      description: `No Phase 18 rule set is defined for “${samskaraType}”; no samskara-specific conclusion was drawn.`,
      supportedSamskara: false,
      provenanceStatus: 'UNVERIFIED',
      confidence: 'INSUFFICIENT',
      ruleEvidence: {
        tithiNum: panchang.tithi.number || 1,
        nakshatraName: panchang.nakshatra.name || '',
        varaName: typeof panchang.vara === 'string' ? panchang.vara : (panchang.vara as any)?.name || 'Unknown',
        matchesConfiguredTithiPreference: false,
        matchesConfiguredNakshatraPreference: false
      }
    };
  }

  const tithiNum = panchang.tithi.number || 1;
  const nakshatraName = panchang.nakshatra.name || '';
  const varaName = typeof panchang.vara === 'string' ? panchang.vara : (panchang.vara as any)?.name || 'Unknown';

  const isApprovedTithi = rules.approvedTithis.includes(tithiNum) || rules.approvedTithis.includes((tithiNum % 15) || 15);
  const isApprovedNakshatra = rules.approvedNakshatras.some(n => nakshatraName.toLowerCase().includes(n.toLowerCase()));

  return {
    samskaraName: samskaraType,
    classification: 'INFORMATIONAL',
    eligibility: 'INFORMATIONAL',
    scoreImpact: 0,
    description: `${samskaraType} preferences are configured but lack approved classical provenance; no auspiciousness or exclusion conclusion is drawn. Current Panchanga: Tithi ${tithiNum}, Nakshatra ${nakshatraName}.`,
    supportedSamskara: true,
    provenanceStatus: 'UNVERIFIED',
    confidence: 'INSUFFICIENT',
    ruleEvidence: {
      tithiNum,
      nakshatraName,
      varaName,
      matchesConfiguredTithiPreference: isApprovedTithi,
      matchesConfiguredNakshatraPreference: isApprovedNakshatra
    }
  };
}
